import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../lib/api.js';
import { PageHeader } from '../components/ui/Feedback.jsx';
import {
    ChevronLeft, ChevronRight, ChevronDown, ChevronRight as ChevronRightIcon,
    Search, X, User, Link2, Cog,
} from 'lucide-react';

// =============================================================================
// AuditLogPage - owner-only read view over the append-only audit_log table
// =============================================================================
function cn(...classes) {
    return classes.filter(Boolean).join(' ');
}

const PAGE_SIZE = 50;

const ENTITY_ROUTES = {
    booking: (id) => `/bookings/${id}`,
};

function prettyAction(action) {
    return action
        .replace(/_/g, ' ')
        .replace(/\./g, ' · ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatTimestamp(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}

export default function AuditLogPage() {
    const api = useApi();
    const navigate = useNavigate();

    const [actionFilter, setActionFilter] = useState('');
    const [entityType, setEntityType] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [search, setSearch] = useState('');
    const [searchApplied, setSearchApplied] = useState('');
    const [offset, setOffset] = useState(0);

    useEffect(() => {
        const t = setTimeout(() => {
            setSearchApplied(search);
            setOffset(0);
        }, 350);
        return () => clearTimeout(t);
    }, [search]);

    const actionsQuery = useQuery({
        queryKey: ['audit', 'actions'],
        queryFn: () => api.get('/audit/actions'),
        staleTime: 10 * 60 * 1000,
    });
    const actionOptions = actionsQuery.data?.actions || [];

    const entityTypeOptions = useMemo(() => {
        const inferred = new Set();
        for (const a of actionOptions) {
            const prefix = a.split('.')[0];
            if (prefix) inferred.add(prefix);
        }
        return ['', ...Array.from(inferred).sort()];
    }, [actionOptions]);

    const queryParams = useMemo(() => {
        const p = { limit: PAGE_SIZE, offset };
        if (actionFilter) p.action = actionFilter;
        if (entityType) p.entity_type = entityType;
        if (dateFrom) p.date_from = dateFrom;
        if (dateTo) p.date_to = dateTo;
        if (searchApplied) p.search = searchApplied;
        return p;
    }, [actionFilter, entityType, dateFrom, dateTo, searchApplied, offset]);

    const { data, isLoading, isError, error } = useQuery({
        queryKey: ['audit', 'list', queryParams],
        queryFn: () => api.get('/audit', { query: queryParams }),
        keepPreviousData: true,
    });

    const entries = data?.entries || [];
    const total = data?.total ?? 0;
    const pageStart = total === 0 ? 0 : offset + 1;
    const pageEnd = Math.min(offset + entries.length, total);

    const isFiltered = Boolean(actionFilter || entityType || dateFrom || dateTo || searchApplied);

    const resetFilters = () => {
        setActionFilter('');
        setEntityType('');
        setDateFrom('');
        setDateTo('');
        setSearch('');
        setSearchApplied('');
        setOffset(0);
    };

    return (
        <div>
            <PageHeader
                title="Audit Log"
                subtitle={total > 0 ? `${total.toLocaleString()} entries · showing ${pageStart}–${pageEnd}` : 'Append-only activity record'}
            />

            {/* Filter bar */}
            <div className="card card-body flex flex-wrap items-end gap-3">
                <div className="space-y-1">
                    <label className="block text-xs font-medium text-ink-subtle uppercase tracking-wide">Action</label>
                    <select
                        value={actionFilter}
                        onChange={(e) => { setActionFilter(e.target.value); setOffset(0); }}
                        className="text-sm px-2 py-1.5 border border-paper-border rounded bg-paper-card min-w-[180px]"
                    >
                        <option value="">All actions</option>
                        {actionOptions.map((a) => (
                            <option key={a} value={a}>{prettyAction(a)}</option>
                        ))}
                    </select>
                </div>

                <div className="space-y-1">
                    <label className="block text-xs font-medium text-ink-subtle uppercase tracking-wide">Entity</label>
                    <select
                        value={entityType}
                        onChange={(e) => { setEntityType(e.target.value); setOffset(0); }}
                        className="text-sm px-2 py-1.5 border border-paper-border rounded bg-paper-card"
                    >
                        {entityTypeOptions.map((t) => (
                            <option key={t || 'any'} value={t}>{t === '' ? 'Any' : t}</option>
                        ))}
                    </select>
                </div>

                <div className="space-y-1">
                    <label className="block text-xs font-medium text-ink-subtle uppercase tracking-wide">From</label>
                    <input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => { setDateFrom(e.target.value); setOffset(0); }}
                        className="text-sm px-2 py-1.5 border border-paper-border rounded bg-paper-card"
                    />
                </div>
                <div className="space-y-1">
                    <label className="block text-xs font-medium text-ink-subtle uppercase tracking-wide">To</label>
                    <input
                        type="date"
                        value={dateTo}
                        onChange={(e) => { setDateTo(e.target.value); setOffset(0); }}
                        className="text-sm px-2 py-1.5 border border-paper-border rounded bg-paper-card"
                    />
                </div>

                <div className="space-y-1 flex-1 min-w-[160px]">
                    <label className="block text-xs font-medium text-ink-subtle uppercase tracking-wide">Search notes</label>
                    <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-subtle pointer-events-none" />
                        <input
                            type="search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Match notes…"
                            className="w-full text-sm pl-8 pr-2 py-1.5 border border-paper-border rounded bg-paper-card focus:outline-none focus:border-ink"
                        />
                    </div>
                </div>

                {isFiltered && (
                    <button
                        type="button"
                        onClick={resetFilters}
                        className="inline-flex items-center gap-1 px-2 py-1.5 text-sm text-ink-muted hover:text-ink"
                    >
                        <X className="h-3.5 w-3.5" />
                        Clear
                    </button>
                )}
            </div>

            {/* Results */}
            <div className="card mt-4 overflow-hidden">
                {isLoading && !data ? (
                    <div className="px-6 py-16 text-center text-ink-subtle text-sm">Loading audit log…</div>
                ) : isError && !data ? (
                    <div className="px-6 py-16 text-center text-sm text-status-danger">
                        {error?.message || 'Could not load the audit log'}
                    </div>
                ) : entries.length === 0 ? (
                    <div className="px-6 py-16 text-center text-ink-subtle text-sm">
                        No audit entries match these filters.
                    </div>
                ) : (
                    <ul className="divide-y divide-paper-border-soft">
                        {entries.map((e) => (
                            <AuditRow key={e.id} entry={e} navigate={navigate} />
                        ))}
                    </ul>
                )}

                {total > PAGE_SIZE && (
                    <div className="px-4 py-3 border-t border-paper-border-soft flex items-center justify-between">
                        <div className="text-xs text-ink-subtle">
                            Showing {pageStart}–{pageEnd} of {total.toLocaleString()}
                        </div>
                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                disabled={offset === 0}
                                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                                className="p-2 rounded hover:bg-paper-hover disabled:opacity-40 disabled:cursor-not-allowed"
                                aria-label="Previous page"
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </button>
                            <button
                                type="button"
                                disabled={pageEnd >= total}
                                onClick={() => setOffset(offset + PAGE_SIZE)}
                                className="p-2 rounded hover:bg-paper-hover disabled:opacity-40 disabled:cursor-not-allowed"
                                aria-label="Next page"
                            >
                                <ChevronRight className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// =============================================================================
// AuditRow - one entry, expandable to show before/after JSON
// =============================================================================
function AuditRow({ entry, navigate }) {
    const [expanded, setExpanded] = useState(false);

    const actorLabel =
        entry.actor_type === 'user'
            ? (entry.actor_name || entry.actor_email || 'User')
            : entry.actor_type === 'magic_link'
                ? 'Chauffeur (via link)'
                : 'System';

    const ActorIcon =
        entry.actor_type === 'user' ? User
        : entry.actor_type === 'magic_link' ? Link2
        : Cog;

    const routeBuilder = ENTITY_ROUTES[entry.entity_type];
    const hasDetail = Boolean(routeBuilder && entry.entity_id);
    const hasPayload = Boolean(entry.before_json || entry.after_json || entry.actor_ip);

    return (
        <li className="hover:bg-paper-hover/50 transition-colors">
            <button
                type="button"
                onClick={() => hasPayload && setExpanded((v) => !v)}
                className={cn(
                    'w-full text-left px-4 py-3 flex items-start gap-3',
                    hasPayload ? 'cursor-pointer' : 'cursor-default'
                )}
            >
                <div className="pt-0.5 w-4 flex-shrink-0">
                    {hasPayload && (
                        expanded
                            ? <ChevronDown className="h-4 w-4 text-ink-subtle" />
                            : <ChevronRightIcon className="h-4 w-4 text-ink-subtle" />
                    )}
                </div>

                <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-ink">{prettyAction(entry.action)}</span>
                        <span className="text-xs text-ink-subtle">·</span>
                        <span className="text-xs text-ink-subtle">{entry.entity_type}</span>
                        {entry.entity_id && (
                            hasDetail ? (
                                <button
                                    type="button"
                                    onClick={(ev) => { ev.stopPropagation(); navigate(routeBuilder(entry.entity_id)); }}
                                    className="font-mono text-xs text-ink underline decoration-dotted hover:decoration-solid"
                                >
                                    {String(entry.entity_id).slice(0, 8)}…
                                </button>
                            ) : (
                                <span className="font-mono text-xs text-ink-subtle">{String(entry.entity_id).slice(0, 8)}…</span>
                            )
                        )}
                    </div>

                    {entry.notes && (
                        <div className="text-sm text-ink-muted">{entry.notes}</div>
                    )}

                    <div className="flex items-center gap-2 text-xs text-ink-subtle flex-wrap">
                        <ActorIcon className="h-3 w-3" />
                        <span>{actorLabel}</span>
                        <span>·</span>
                        <span>{formatTimestamp(entry.occurred_at)}</span>
                    </div>
                </div>
            </button>

            {expanded && hasPayload && (
                <div className="px-4 pb-4 pl-11 space-y-3">
                    {entry.actor_ip && (
                        <div className="text-xs text-ink-subtle">
                            IP: <span className="font-mono text-ink">{entry.actor_ip}</span>
                        </div>
                    )}
                    {entry.before_json && <JsonBlock label="Before" data={entry.before_json} />}
                    {entry.after_json && <JsonBlock label="After" data={entry.after_json} />}
                </div>
            )}
        </li>
    );
}

function JsonBlock({ label, data }) {
    let pretty;
    try {
        pretty = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
    } catch {
        pretty = String(data);
    }
    return (
        <div>
            <div className="text-xs font-medium text-ink-subtle uppercase tracking-wide mb-1">{label}</div>
            <pre className="text-xs bg-paper-hover/40 border border-paper-border-soft rounded-lg p-3 overflow-x-auto text-ink whitespace-pre-wrap break-words">
                {pretty}
            </pre>
        </div>
    );
}
